// 📁 localBackup.js - Fonctions pour sauvegarder sur ton serveur local Mac

const LOCAL_SERVER_URL = 'https://hust-server-v2-production.up.railway.app/api'; // ✅ PORT 3002

// 🔧 Fonction pour tester si le serveur local est disponible
export const isLocalServerAvailable = async () => {
  try {
    const response = await fetch(`${LOCAL_SERVER_URL}/../`, {
      method: 'GET'
    });
    return response.ok;
  } catch (error) {
    console.log('⚠️ Serveur local non disponible:', error.message);
    return false;
  }
};

// 💾 Sauvegarder un utilisateur sur le serveur local
export const saveUserToLocal = async (userData) => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      console.log('⚠️ Serveur local non disponible - sauvegarde ignorée');
      return { success: false, reason: 'server_unavailable' };
    }

    const response = await fetch(`${LOCAL_SERVER_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    });

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Utilisateur sauvegardé sur serveur local:', userData.email);
    } else {
      console.log('❌ Erreur sauvegarde serveur local:', result.message);
    }
    
    return result;
  } catch (error) {
    console.log('❌ Erreur connexion serveur local:', error.message);
    return { success: false, error: error.message };
  }
};

// 🔄 Mettre à jour un utilisateur sur le serveur local
export const updateUserOnLocal = async (email, userData) => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      console.log('⚠️ Serveur local non disponible - mise à jour ignorée');
      return { success: false, reason: 'server_unavailable' };
    }

    const response = await fetch(`${LOCAL_SERVER_URL}/users/${email}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    });

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Utilisateur mis à jour sur serveur local:', email);
    } else {
      console.log('❌ Erreur mise à jour serveur local:', result.message);
    }
    
    return result;
  } catch (error) {
    console.log('❌ Erreur connexion serveur local:', error.message);
    return { success: false, error: error.message };
  }
};

// 📖 Récupérer tous les utilisateurs du serveur local
export const getAllUsersFromLocal = async () => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      return { success: false, reason: 'server_unavailable' };
    }

    const response = await fetch(`${LOCAL_SERVER_URL}/users`);
    const result = await response.json();
    
    return result;
  } catch (error) {
    console.log('❌ Erreur récupération utilisateurs:', error.message);
    return { success: false, error: error.message };
  }
};

// 🔄 Synchroniser tous les utilisateurs localStorage vers serveur local
export const syncAllUsersToLocal = async () => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      console.log('⚠️ Serveur local non disponible - synchronisation ignorée');
      return { success: false, reason: 'server_unavailable' };
    }

    // Récupérer tous les utilisateurs de localStorage
    const allUsers = JSON.parse(localStorage.getItem('allUsers')) || {};
    
    const response = await fetch(`${LOCAL_SERVER_URL}/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ users: allUsers })
    });

    const result = await response.json();
    
    if (result.success) {
      console.log(`✅ Synchronisation réussie - ${result.count} utilisateurs sauvegardés`);
    } else {
      console.log('❌ Erreur synchronisation:', result.message);
    }
    
    return result;
  } catch (error) {
    console.log('❌ Erreur synchronisation:', error.message);
    return { success: false, error: error.message };
  }
};

// 📊 Récupérer les statistiques du serveur local
export const getLocalStats = async () => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      return { success: false, reason: 'server_unavailable' };
    }

    const response = await fetch(`${LOCAL_SERVER_URL}/stats`);
    const result = await response.json();
    
    return result;
  } catch (error) {
    console.log('❌ Erreur récupération stats:', error.message);
    return { success: false, error: error.message };
  }
};

// 🔄 Fonction hybride : sauvegarde localStorage + serveur local
export const saveUserHybrid = async (userData) => {
  // 1. Sauvegarder dans localStorage (priorité)
  const allUsers = JSON.parse(localStorage.getItem('allUsers')) || {};
  allUsers[userData.email.toLowerCase()] = userData;
  localStorage.setItem('allUsers', JSON.stringify(allUsers));
  
  // 2. Sauvegarder sur serveur local (backup)
  const localResult = await saveUserToLocal(userData);
  
  return {
    localStorage: true,
    localServer: localResult.success,
    userData: userData
  };
};

// 🔄 Fonction hybride : mise à jour localStorage + serveur local
export const updateUserHybrid = async (email, userData) => {
  // 1. Mettre à jour localStorage (priorité)
  const allUsers = JSON.parse(localStorage.getItem('allUsers')) || {};
  allUsers[email.toLowerCase()] = userData;
  localStorage.setItem('allUsers', JSON.stringify(allUsers));
  
  // 2. Mettre à jour serveur local (backup)
  const localResult = await updateUserOnLocal(email, userData);
  
  return {
    localStorage: true,
    localServer: localResult.success,
    userData: userData
  };
};

// 🔄 Restaurer localStorage depuis le serveur local
export const restoreFromLocal = async () => {
  try {
    const result = await getAllUsersFromLocal();
    
    if (result.success && result.users) {
      localStorage.setItem('allUsers', JSON.stringify(result.users));
      console.log('✅ localStorage restauré depuis le serveur local');
      return { success: true, count: Object.keys(result.users).length };
    } else {
      console.log('⚠️ Aucune donnée à restaurer');
      return { success: false, reason: 'no_data' };
    }
  } catch (error) {
    console.log('❌ Erreur restauration:', error.message);
    return { success: false, error: error.message };
  }
};


// 📖 Récupérer un utilisateur du serveur local
export const getUserFromLocal = async (email) => {
  try {
    const serverAvailable = await isLocalServerAvailable();
    if (!serverAvailable) {
      return { success: false, reason: 'server_unavailable' };
    }

    const response = await fetch(`${LOCAL_SERVER_URL}/users/${email}`);
    const result = await response.json();
    
    return result;
  } catch (error) {
    console.log('❌ Erreur récupération utilisateur:', error.message);
    return { success: false, error: error.message };
  }
};


